import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, FormControl, FormGroup, FormHelperText, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { ColorPalette } from '../../../../../components/config';
import { FormDialog } from '../../../../../components/form-dialog/form-dialog';
import { Row } from '../../../../../components/row/row';
import { Cell } from '../../../../../components/cell/cell';
import { TextField } from '../../../../../components/text-field/text-field';
import { Checkbox } from '../../../../../components/checkbox/checkbox';
import { MultiFilePicker } from '../../../../../components/multi-file-picker/multi-file-picker';
import { forumAttachmentMaxSizeInBytes, forumAttachmentMimeTypes, forumNewChatAttachmentsMaxTotalSizeInBytes } from '../../../../forum/forum-attachment-rules';

const maximumTitleLength = 200;
const maximumMessageLength = 4000;
const confidentialChatExplanation =
  'Vælger du at oprette en fortrolig chat, vil brugere, der ikke er en del af chatten, ikke kunne se chattens titel og beskeder i chatten. ' +
  'Det er ikke muligt at tilføje brugere til en fortrolig chat, efter den er oprettet.';

const attachmentLabels = {
  dropZoneHelperText: 'Billeder, PDF og tekstfiler på maks. 10MB',
  selectFilesButton: 'Vedhæft filer',
  invalidFileTypeError: 'Det er ikke tilladt at vedhæfte denne filtype',
  fileTooLargeError: (fileName: string) => `"${fileName}" er større end de tilladte 10MB`,
};

const participantsGroupSx = {
  m: 0,
  px: '14px',
  pt: 0,
  pb: 1,
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: '4px',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const participantsLegendSx = {
  px: '5px',
  fontSize: '0.75rem',
  color: 'text.secondary',
};

export interface ISelectableItem {
  id: string;
  title: string;
}

export interface ICreateChatResult {
  title: string;
  caseId: string;
  contactIds: string[];
  message: string;
  isConfidential: boolean;
  attachments: File[];
}

export interface ICreateChatDialogProps {
  open: boolean;
  selectableCases: ISelectableItem[];
  selectableContacts: ISelectableItem[];
  confidentialChatsEnabled: boolean;
  citizenPicker?: React.ReactNode;
  hasChosenCitizen?: boolean;
  onSave: (result: ICreateChatResult) => void | Promise<void>;
  onCancel: () => void;
}

interface CreateChatDialogErrors {
  citizen?: string;
  title?: string;
  caseId?: string;
  message?: string;
}

export function CreateChatDialog(props: ICreateChatDialogProps) {
  const { open, selectableCases, selectableContacts, confidentialChatsEnabled, citizenPicker, hasChosenCitizen = true, onSave, onCancel } = props;

  const [title, setTitle] = useState('');
  const [caseId, setCaseId] = useState('');
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [isConfidential, setIsConfidential] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [errors, setErrors] = useState<CreateChatDialogErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const onlySelectableCaseId = selectableCases.length === 1 ? selectableCases[0].id : '';
  const isSelectableCase = selectableCases.some(selectableCase => selectableCase.id === caseId);
  const selectableOptionsKey = `${selectableCases.map(selectableCase => selectableCase.id).join()}|${selectableContacts.map(contact => contact.id).join()}`;

  useEffect(() => {
    setCaseId(onlySelectableCaseId);
    setContactIds([]);
    // Another citizen means other cases and contacts, so nothing chosen for the previous one may stay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableOptionsKey]);

  useEffect(() => {
    if (hasChosenCitizen) {
      setErrors(current => ({ ...current, citizen: undefined }));
    }
  }, [hasChosenCitizen]);

  useEffect(() => {
    if (open) {
      setTitle('');
      setCaseId(onlySelectableCaseId);
      setContactIds([]);
      setMessage('');
      setIsConfidential(false);
      setAttachments([]);
      setAttachmentError('');
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function validate() {
    const found: CreateChatDialogErrors = {};

    if (!hasChosenCitizen) {
      found.citizen = 'Der skal vælges en borger.';
    }
    if (!title.trim()) {
      found.title = 'Titel skal være udfyldt.';
    }
    if (!caseId) {
      found.caseId = 'Der skal være en sag tilknyttet en Besked.';
    }
    if (!message.trim()) {
      found.message = 'Besked skal være udfyldt.';
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  }

  async function handleSave() {
    if (isSavingRef.current || !validate()) {
      return;
    }
    if (attachments.reduce((total, file) => total + file.size, 0) > forumNewChatAttachmentsMaxTotalSizeInBytes) {
      setAttachmentError('Vedhæftningerne fylder tilsammen mere end 20MB. Fjern nogle filer, og send dem som beskeder i chatten bagefter.');
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onSave({ title: title.trim(), caseId, contactIds, message: message.trim(), isConfidential, attachments });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  function handleMessageKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' && event.ctrlKey && !event.repeat) {
      event.preventDefault();
      handleSave();
    }
  }

  function handleContactToggle(contactId: string) {
    setContactIds(current => (current.includes(contactId) ? current.filter(id => id !== contactId) : [...current, contactId]));
  }

  return (
    <FormDialog
      open={open}
      title="Opret besked"
      confirmButtonText="Opret"
      cancelButtonText="Annullér"
      onConfirmClick={handleSave}
      isLoading={isSaving}
      onClose={onCancel}
      onCancelClick={onCancel}
      dontCapitalizeButtonTexts
      fullWidth
    >
      <FormControl fullWidth>
        {citizenPicker && (
          <Row>
            <Cell size="full">
              {citizenPicker}
              {errors.citizen && <FormHelperText error>{errors.citizen}</FormHelperText>}
            </Cell>
          </Row>
        )}
        {confidentialChatsEnabled && (
          <Row>
            <Cell size="full">
              <Stack direction="row" alignItems="center">
                <Checkbox checked={isConfidential} onChange={() => setIsConfidential(!isConfidential)} label="Fortrolig Chat" />
                <Tooltip title={confidentialChatExplanation}>
                  <Box component="span" aria-label="Hjælp til fortrolig chat" sx={{ display: 'inline-flex', color: ColorPalette.InfoIcon }}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </Box>
                </Tooltip>
              </Stack>
            </Cell>
          </Row>
        )}
        <Row>
          <Cell size="full">
            <TextField
              label="TITEL PÅ BESKED"
              value={title}
              variant="outlined"
              multiline={false}
              required
              error={!!errors.title}
              errorText={errors.title}
              maxLength={maximumTitleLength}
              helperText={`${maximumTitleLength - title.length} tegn tilbage`}
              onChange={event => setTitle(event.target.value)}
            />
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <TextField
              label="SAG I FASIT"
              value={isSelectableCase ? caseId : ''}
              select
              variant="outlined"
              multiline={false}
              required
              error={!!errors.caseId}
              errorText={errors.caseId}
              onChange={event => setCaseId(event.target.value)}
            >
              {selectableCases.map(selectableCase => (
                <MenuItem key={selectableCase.id} value={selectableCase.id}>
                  {selectableCase.title}
                </MenuItem>
              ))}
            </TextField>
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <Box component="fieldset" sx={participantsGroupSx}>
              <Box component="legend" sx={participantsLegendSx}>
                DELTAGERE
              </Box>
              <FormGroup>
                {selectableContacts.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Ingen deltagere at vælge
                  </Typography>
                )}
                {selectableContacts.map(contact => (
                  <Checkbox key={contact.id} checked={contactIds.includes(contact.id)} onChange={() => handleContactToggle(contact.id)} label={contact.title} />
                ))}
              </FormGroup>
            </Box>
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <TextField
              label="SKRIV DIN BESKED HER"
              value={message}
              variant="outlined"
              multiline
              minRows={4}
              required
              error={!!errors.message}
              errorText={errors.message}
              maxLength={maximumMessageLength}
              helperText={`${maximumMessageLength - message.length} tegn tilbage`}
              onChange={event => setMessage(event.target.value)}
              onKeyDown={handleMessageKeyDown}
            />
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <MultiFilePicker
              files={attachments}
              onFilesChange={setAttachments}
              error={attachmentError}
              onErrorChange={setAttachmentError}
              acceptedMimeTypes={forumAttachmentMimeTypes}
              acceptAttribute={forumAttachmentMimeTypes.join(',')}
              maxFileSizeBytes={forumAttachmentMaxSizeInBytes}
              disabled={isSaving}
              labels={attachmentLabels}
            />
          </Cell>
        </Row>
      </FormControl>
    </FormDialog>
  );
}
